import { Check, Fence, AlertTriangle, Trees, Car, Droplets, Camera, Users } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { PageHero } from "../components/layout/PageHero";
import { CtaBand } from "../components/layout/CtaBand";

const INCLUDES = [
  { icon: Car, text: "Collection and drop-off included with group walks" },
  { icon: Users, text: "Careful introductions and considered group matching" },
  { icon: Trees, text: "Mary's Meadow, other fenced fields and selected forest trails" },
  { icon: Droplets, text: "Fresh water and appropriate breaks" },
  { icon: Camera, text: "Photo and video updates where practical" },
];

export default function DogWalkingPage() {
  return (
    <PageShell title="Dog Walking">
      <PageHero
        testId="walking-hero"
        eyebrow="Dog Walking · Inverness"
        title="Regular walks, carefully matched company."
        copy="Plenty of countryside space, personally delivered by Carly, with Mary's Meadow at the heart of the experience."
        image="/images/marypawpins/dogs-running-viaduct-panorama.webp"
        alt="Three dogs charging across the field at Mary's Meadow with Culloden Viaduct behind"
        ctaText="Enquire About a Regular Place"
        ctaTo="/contact?service=Dog%20Walking"
        note="Monday · Tuesday · Friday"
      />

      <section className="py-14 sm:py-20" data-testid="walking-meadow">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">Mary's Meadow</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">
              4.6 acres. Plenty of room to be a dog.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#575953]">
              Mary's Meadow is a private field beneath Culloden Viaduct, a distinctive
              setting for regular group walks, with space for suitable dogs to run, play,
              sniff and explore. Other fully fenced fields are used occasionally for a
              change of scenery.
            </p>
            <div className="mt-6 rounded-2xl border border-[#DDD7CB] bg-[#E7E2D8]/50 p-5 flex gap-4">
              <Fence className="w-5 h-5 text-[#3F4A38] flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed text-[#575953]">
                Our private field is fully fenced, although the fencing is not six
                feet high. Dogs with a history of jumping, climbing or escaping are
                assessed individually to make sure the space is suitable for them.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img
                src="/images/marypawpins/cockapoo-closeup-smile.webp"
                alt="A very happy dog in the grass at Mary's Meadow"
                className="rounded-3xl w-full aspect-[3/4] object-cover shadow-[0_20px_60px_rgba(41,42,38,0.15)]"
                data-testid="meadow-photo"
              />
              <video
                src="/videos/meadow-pack-play.mp4"
                poster="/videos/meadow-pack-play.jpg"
                autoPlay muted loop playsInline
                className="rounded-3xl w-full aspect-[3/4] object-cover shadow-[0_20px_60px_rgba(41,42,38,0.15)] sm:mt-10"
                data-testid="meadow-video-2"
              />
            </div>
            <p className="mt-3 text-xs text-[#575953] text-center">Room to run. The Meadow in full swing.</p>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#EFECE3]" data-testid="walking-services">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="eyebrow">The walks</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">Group and solo.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <article className="bg-white rounded-3xl border border-[#DDD7CB] overflow-hidden" data-testid="group-walk-card">
              <img src="/images/marypawpins/group-buttercup-meadow.webp" alt="A group of dogs in the buttercup meadow" className="w-full aspect-[16/9] object-cover" />
              <div className="p-7 lg:p-8">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-serif text-2xl font-medium text-[#292A26]">Group walks</h3>
                  <p className="font-serif text-2xl text-[#3F4A38]" data-testid="group-walk-price">£18 <span className="text-sm text-[#575953] font-sans">/ hour</span></p>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                  For dogs who enjoy suitable canine company. Dogs are introduced and
                  grouped with consideration for temperament, energy, play style and
                  compatibility. Groups are chosen to suit the dogs, never filled to
                  capacity. Collection and drop-off included.
                </p>
              </div>
            </article>
            <article className="bg-white rounded-3xl border border-[#DDD7CB] overflow-hidden" data-testid="solo-walk-card">
              <img src="/images/marypawpins/dachshund-forest-logs.jpg" alt="A dog enjoying a quieter solo walk in the forest" className="w-full aspect-[16/9] object-cover" />
              <div className="p-7 lg:p-8">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-serif text-2xl font-medium text-[#292A26]">Solo walks</h3>
                  <p className="font-serif text-2xl text-[#3F4A38]" data-testid="solo-walk-price">£22 <span className="text-sm text-[#575953] font-sans">/ hour</span></p>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                  One-to-one walks for dogs who benefit from individual attention, a
                  different pace or an environment that doesn't suit a group, such as senior
                  dogs, nervous dogs or those with particular needs, subject to
                  assessment and availability.
                </p>
              </div>
            </article>
          </div>
          <p className="mt-6 text-sm text-[#575953]">
            Two dogs from the same household? Just ask. Carly will confirm arrangements and pricing when you enquire.
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-20" data-testid="walking-includes">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">The details</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">What a walk includes.</h2>
            <p className="mt-5 text-base leading-relaxed text-[#575953]">
              A full hour, plus everything around it handled properly.
            </p>
            <div className="mt-6 rounded-2xl border border-[#DDD7CB] bg-[#F4ECE9] p-5 flex gap-4">
              <Trees className="w-5 h-5 text-[#3F4A38] flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed text-[#575953]">
                Forest trails are for selected smaller groups whose recall and suitability
                Carly is confident in. Off-lead decisions always depend on the individual
                dog, the surroundings and what we agree together.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {INCLUDES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 bg-white rounded-2xl border border-[#DDD7CB] p-5">
                  <span className="w-9 h-9 rounded-full bg-[#F4ECE9] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#3F4A38]" />
                  </span>
                  <span className="text-sm leading-relaxed text-[#575953] pt-1.5">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#3F4A38] text-[#F7F4ED]" data-testid="walking-regular">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="eyebrow-light">Regular places</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight mt-3">Mondays, Tuesdays and Fridays.</h2>
            <p className="mt-5 text-base leading-relaxed text-[#F7F4ED]/80">
              Mary Pawpins is prioritising regular walking clients on these three days.
              Recurring slots are reserved for regular clients, so your dog keeps the
              same rhythm and the same walker every week.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-[#F7F4ED]/15 bg-[#F7F4ED]/5 p-6">
              <h3 className="font-serif text-xl">Suitability &amp; introductions</h3>
              <ul className="mt-4 space-y-2.5 text-sm text-[#F7F4ED]/75">
                {[
                  "Every new dog starts with a chat and a free meet & greet",
                  "Temperament, recall, sociability, health and mobility all considered",
                  "Not every dog suits group walks; if not, Carly will discuss alternatives honestly",
                  "Groups always managed within insurance conditions",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-[#D8B7B0] flex-shrink-0 mt-0.5" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20" data-testid="walking-safety">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 max-w-4xl">
          <div className="rounded-3xl border border-[#DDD7CB] bg-[#E7E2D8]/50 p-7 sm:p-9 flex gap-5">
            <AlertTriangle className="w-6 h-6 text-[#3F4A38] flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-serif text-2xl font-medium text-[#292A26]">Safety and insurance, plainly.</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                Mary Pawpins holds commercial pet-business insurance, including public
                liability and animal injury cover. Groups are managed within insurance
                conditions and according to suitability. Group sizes are chosen for the
                dogs involved, not set to maximum limits. Details of cover are available
                on request.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        testId="walking-cta"
        heading="Looking for a regular place?"
        copy="Tell Carly about your dog, where you are based and which days you need. She can then discuss availability and whether the group would be a good fit."
        ctaTo="/contact?service=Dog%20Walking"
        buttonText="Enquire About Dog Walking"
      />
    </PageShell>
  );
}
